/**
 * FIELD-001C1B (clarification) · Holiday period helpers.
 *
 * The engineers collection stores `holidays` as a flat, sorted array of
 * ISO date strings (`YYYY-MM-DD`). We keep that on-disk shape (no schema
 * migration) but expose consecutive-day *periods* in the UI so users can
 * add real absences ("30 Aug – 5 Sep 2026") without inventing a parallel
 * data model.
 *
 *   • {@link expandPeriodsToDates} — turn `{start,end}` objects into the
 *     inclusive list of ISO dates the backend expects.
 *   • {@link groupDatesToPeriods} — regroup a flat list of ISO dates into
 *     consecutive-day periods for display.
 *   • {@link formatPeriod} — human-readable label for a period.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function isValidIso(s) {
    if (typeof s !== "string" || !ISO_RE.test(s)) return false;
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

function isoToUtc(s) {
    return new Date(`${s}T00:00:00Z`);
}

function utcToIso(d) {
    return d.toISOString().slice(0, 10);
}

function dayDiff(aIso, bIso) {
    return Math.round((isoToUtc(bIso) - isoToUtc(aIso)) / MS_PER_DAY);
}

/**
 * Expand `[{start, end}]` into a flat, sorted, de-duplicated array of ISO
 * dates. Invalid ranges (missing dates or `end < start`) are dropped.
 */
export function expandPeriodsToDates(periods) {
    const out = new Set();
    for (const p of periods || []) {
        if (!p || !isValidIso(p.start) || !isValidIso(p.end)) continue;
        if (p.end < p.start) continue;
        let cursor = isoToUtc(p.start);
        const end = isoToUtc(p.end);
        while (cursor.getTime() <= end.getTime()) {
            out.add(utcToIso(cursor));
            cursor = new Date(cursor.getTime() + MS_PER_DAY);
        }
    }
    return Array.from(out).sort();
}

/**
 * Group a flat list of ISO dates into consecutive-day periods.
 * Non-ISO or duplicate entries are ignored.
 */
export function groupDatesToPeriods(dates) {
    const clean = Array.from(new Set((dates || []).filter(isValidIso))).sort();
    const periods = [];
    for (const d of clean) {
        const last = periods[periods.length - 1];
        if (last && dayDiff(last.end, d) === 1) {
            last.end = d;
        } else {
            periods.push({ start: d, end: d });
        }
    }
    return periods;
}

function formatIsoShort(iso, { withYear }) {
    const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
    const day = d; // no leading zero in short form
    const month = MONTH_SHORT[m - 1] || "";
    return withYear ? `${day} ${month} ${y}` : `${day} ${month}`;
}

/**
 * Human-readable label for a period. Examples:
 *   { start: "2026-08-30", end: "2026-08-30" } → "30 Aug 2026"
 *   { start: "2026-08-30", end: "2026-09-05" } → "30 Aug – 5 Sep 2026"
 *   Cross-year → "30 Dec 2026 – 2 Jan 2027"
 */
export function formatPeriod(period) {
    if (!period || !isValidIso(period.start) || !isValidIso(period.end)) return "";
    if (period.start === period.end) {
        return formatIsoShort(period.start, { withYear: true });
    }
    const [ys] = period.start.split("-");
    const [ye] = period.end.split("-");
    if (ys === ye) {
        return `${formatIsoShort(period.start, { withYear: false })} – ${formatIsoShort(period.end, { withYear: true })}`;
    }
    return `${formatIsoShort(period.start, { withYear: true })} – ${formatIsoShort(period.end, { withYear: true })}`;
}

/** Stable id for a period, useful as a testid segment or React key. */
export function periodKey(period) {
    return `${period.start}--${period.end}`;
}
